/**
 * AI Conference App - Main Application
 *
 * Mobile-first conference management app (450px max-width)
 * Dark theme, #3713EC purple primary, Inter font
 */

import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router';
import { AnimatePresence, motion, MotionConfig } from 'motion/react';
import { Toaster } from 'sonner';
import { AppProvider } from './context/AppContext';
import { NotificationProvider } from './context/NotificationContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { WelcomeGuide } from './components/WelcomeGuide';
import { useApp } from './context/AppContext';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import RubikooBrand from './components/RubikooBrand';
import WelcomeScreen from './screens/WelcomeScreen';
import { hasSeenWelcome, markWelcomeSeen } from './utils/welcomeExperience';
import type { EventModuleKey } from '@conference/shared';

// Screen imports
import SignInScreen from './screens/SignInScreen';
import SignUpScreen from './screens/SignUpScreen';
import ForgotPasswordScreen from './screens/ForgotPasswordScreen';
import ResetPasswordScreen from './screens/ResetPasswordScreen';
import ActivateAccountScreen from './screens/ActivateAccountScreen';
import ChangePasswordScreen from './screens/ChangePasswordScreen';
import EventSelectorScreen from './screens/EventSelectorScreen';
import HomeScreen from './screens/HomeScreen';
import AgendaScreen from './screens/AgendaScreen';
import LibraryScreen from './screens/LibraryScreen';
import ExploreScreen from './screens/ExploreScreen';
import SessionDetailScreen from './screens/SessionDetailScreen';
import SessionFeedbackScreen from './screens/SessionFeedbackScreen';
import SpeakerProfileScreen from './screens/SpeakerProfileScreen';
import PaperDetailsScreen from './screens/PaperDetailsScreen';
import ExhibitorDetailsScreen from './screens/ExhibitorDetailsScreen';
import ReportBuilderScreen from './screens/ReportBuilderScreen';
import SessionPrepScreen from './screens/SessionPrepScreen';
import MyNotesScreen from './screens/MyNotesScreen';
import EventDetailScreen from './screens/EventDetailScreen';
import AttendeeDetailsScreen from './screens/AttendeeDetailsScreen';
import ProfileScreen from './screens/ProfileScreen';
import LegalScreen from './screens/LegalScreen';
import NotificationsScreen from './screens/NotificationsScreen';
import MapScreen from './screens/MapScreen';
import ConferenceRecapScreen from './screens/ConferenceRecapScreen';
import VerifyEmailScreen from './screens/VerifyEmailScreen';
import CompleteProfileScreen from './screens/CompleteProfileScreen';
import PolicyConsentScreen from './screens/PolicyConsentScreen';
import EventInterestsScreen from './screens/EventInterestsScreen';
import MyConferencesScreen from './screens/MyConferencesScreen';
import JoinWithCodeScreen from './screens/JoinWithCodeScreen';
import DiscoverScreen from './screens/DiscoverScreen';
import EventPreviewScreen from './screens/EventPreviewScreen';
import ChatHistoryScreen from './screens/ChatHistoryScreen';
import { publicInformationRoutes } from './routes/publicInformationRoutes';
import ModuleRouteGuard from './components/ModuleRouteGuard';
import NativeAuthLinkListener from './components/NativeAuthLinkListener';
import AISharingDialog from './components/AISharingDialog';

/**
 * AnimatedRoutes — lives inside <Router> so it can use useLocation.
 * Uses a "pageKey" that groups tab sub-routes under the same animation key,
 * preventing redundant transitions when switching tabs within Library/Explore.
 */
function getPageKey(pathname: string): string {
  if (pathname.startsWith('/library')) return 'library';
  if (pathname.startsWith('/explore')) return 'explore';
  // Group session sub-routes under same animation key
  if (pathname.match(/^\/session\/[^/]+\/prep$/)) return pathname.replace('/prep', '');
  return pathname;
}

/**
 * Listens for 403-reason events dispatched by apiClient and navigates accordingly.
 * Must be inside Router so useNavigate works.
 */
function Auth403Navigator() {
  const navigate = useNavigate();
  useEffect(() => {
    const onVerify   = () => navigate('/verify-email', { replace: true });
    const onConsent  = () => navigate('/consent', { replace: true });
    const onProfile  = () => navigate('/complete-profile', { replace: true });
    const onJoin     = () => navigate('/my-conferences', { replace: true });
    window.addEventListener('auth:verify_required', onVerify);
    window.addEventListener('auth:consent_required', onConsent);
    window.addEventListener('auth:profile_required', onProfile);
    window.addEventListener('auth:join_required', onJoin);
    return () => {
      window.removeEventListener('auth:verify_required', onVerify);
      window.removeEventListener('auth:consent_required', onConsent);
      window.removeEventListener('auth:profile_required', onProfile);
      window.removeEventListener('auth:join_required', onJoin);
    };
  }, [navigate]);
  return null;
}

/**
 * AuthGate — shows a full-screen splash while checking for an
 * existing session (isAuthLoading = true). Once resolved:
 *   • unauthenticated  → show sign-in / sign-up routes
 *   • authenticated    → redirect away from sign-in to home or event-selector
 */
function AuthGate({ children }: { children: React.ReactNode }) {
  const { isAuthLoading, isAuthenticated, currentEventId } = useApp();

  if (isAuthLoading) {
    return (
      <div
        className="absolute inset-0 flex flex-col items-center justify-center gap-4"
        style={{ backgroundColor: 'var(--app-bg)' }}
      >
        <RubikooBrand />
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--app-accent)' }} />
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Redirects already-authenticated users away from auth screens.
 * Checks verification, current policy acceptance, and profile completion before landing.
 */
function AuthRedirect({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, authUser, currentEventId, policyStatus } = useApp();
  if (isAuthenticated) {
    if (!authUser?.is_verified) return <Navigate to="/verify-email" replace />;
    if (policyStatus?.accepted !== true) return <Navigate to="/consent" replace />;
    if (!authUser?.profile_complete) return <Navigate to="/complete-profile" replace />;
    return <Navigate to={currentEventId ? '/home' : '/my-conferences'} replace />;
  }
  return <>{children}</>;
}

function AppEntry() {
  const { isAuthenticated } = useApp();
  if (isAuthenticated) return <Navigate to="/my-conferences" replace />;
  return <Navigate to={hasSeenWelcome() ? '/signin' : '/welcome'} replace />;
}

/**
 * Protects routes that require authentication.
 * Unauthenticated users are redirected to /signin.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useApp();
  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }
  return <>{children}</>;
}

function RequireCurrentPolicy({ children }: { children: React.ReactNode }) {
  const { authUser, isAuthenticated, policyStatus } = useApp();
  if (!isAuthenticated) return <Navigate to="/signin" replace />;
  if (!authUser?.is_verified) return <Navigate to="/verify-email" replace />;
  if (policyStatus?.accepted !== true) return <Navigate to="/consent" replace />;
  return <>{children}</>;
}

function RequireReadyAccount({
  children,
  requireEvent = false,
}: {
  children: React.ReactNode;
  requireEvent?: boolean;
}) {
  const {
    authUser,
    currentEventId,
    isAuthenticated,
    policyStatus,
  } = useApp();
  if (!isAuthenticated) return <Navigate to="/signin" replace />;
  if (!authUser?.is_verified) return <Navigate to="/verify-email" replace />;
  if (policyStatus?.accepted !== true) return <Navigate to="/consent" replace />;
  if (!authUser.profile_complete) return <Navigate to="/complete-profile" replace />;
  if (requireEvent && !currentEventId) return <Navigate to="/my-conferences" replace />;
  return <>{children}</>;
}

function RequireModule({
  module,
  children,
}: {
  module: EventModuleKey;
  children: React.ReactNode;
}) {
  return (
    <RequireReadyAccount requireEvent>
      <ModuleRouteGuard module={module}>{children}</ModuleRouteGuard>
    </RequireReadyAccount>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  // A second email action on the same path must not reuse the previous token's
  // verification flag or a partially entered password/confirmation form.
  const isEmailAction = ['/verify-email', '/reset-password', '/activate-account'].includes(location.pathname);
  const pageKey = getPageKey(location.pathname) + (isEmailAction ? location.search : '');

  return (
    <AuthGate>
      <Auth403Navigator />
      <AnimatePresence initial={false}>
        <motion.div
          key={pageKey}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0"
        >
          <Routes location={location}>
            {/* Auth — redirect away if already signed in */}
            <Route path="/signin" element={<AuthRedirect><SignInScreen /></AuthRedirect>} />
            <Route path="/welcome" element={<AuthRedirect><WelcomeScreen /></AuthRedirect>} />
            <Route path="/legal" element={<LegalScreen />} />
            {publicInformationRoutes.map((route) => (
              <Route key={route.path} path={route.path} element={route.element} />
            ))}
            <Route path="/signup" element={<AuthRedirect><SignUpScreen /></AuthRedirect>} />
            <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
            <Route path="/reset-password" element={<ResetPasswordScreen />} />
            <Route path="/activate-account" element={<ActivateAccountScreen />} />

            {/* Post-auth onboarding (require auth, allow unverified/incomplete) */}
            <Route path="/verify-email"      element={<VerifyEmailScreen />} />
            <Route path="/consent"          element={<RequireAuth><PolicyConsentScreen /></RequireAuth>} />
            <Route path="/complete-profile" element={<RequireCurrentPolicy><CompleteProfileScreen /></RequireCurrentPolicy>} />
            <Route path="/event-interests"  element={<RequireReadyAccount><EventInterestsScreen /></RequireReadyAccount>} />

            {/* Conference selection & joining */}
            <Route path="/my-conferences"   element={<RequireReadyAccount><MyConferencesScreen /></RequireReadyAccount>} />
            <Route path="/join"             element={<RequireReadyAccount><JoinWithCodeScreen /></RequireReadyAccount>} />
            <Route path="/discover"         element={<RequireReadyAccount><DiscoverScreen /></RequireReadyAccount>} />
            <Route path="/event-preview/:id" element={<RequireReadyAccount><EventPreviewScreen /></RequireReadyAccount>} />

            {/* Protected routes */}
            <Route path="/event-selector" element={<RequireReadyAccount><EventSelectorScreen /></RequireReadyAccount>} />
            <Route path="/home"           element={<RequireReadyAccount requireEvent><HomeScreen /></RequireReadyAccount>} />
            <Route path="/agenda"         element={<RequireModule module="agenda"><AgendaScreen /></RequireModule>} />
            <Route path="/session/:id"      element={<RequireModule module="agenda"><SessionDetailScreen /></RequireModule>} />
            <Route path="/session/:id/prep" element={<RequireModule module="agenda"><ModuleRouteGuard module="ai_copilot"><SessionPrepScreen /></ModuleRouteGuard></RequireModule>} />
            <Route path="/session/:id/feedback" element={<RequireModule module="agenda"><SessionFeedbackScreen /></RequireModule>} />
            <Route path="/paper/:id/feedback" element={<RequireModule module="agenda"><SessionFeedbackScreen target="presentation" /></RequireModule>} />

            <Route path="/library"             element={<RequireReadyAccount requireEvent><LibraryScreen /></RequireReadyAccount>} />
            <Route path="/library/agenda"      element={<RequireModule module="agenda"><LibraryScreen /></RequireModule>} />
            <Route path="/library/papers"      element={<RequireModule module="papers"><LibraryScreen /></RequireModule>} />
            <Route path="/library/speakers"    element={<RequireModule module="speakers"><LibraryScreen /></RequireModule>} />
            <Route path="/library/exhibitors"  element={<RequireModule module="exhibitors"><LibraryScreen /></RequireModule>} />
            <Route path="/library/contacts"    element={<RequireReadyAccount requireEvent><LibraryScreen /></RequireReadyAccount>} />

            <Route path="/explore"             element={<RequireReadyAccount requireEvent><ExploreScreen /></RequireReadyAccount>} />
            <Route path="/explore/papers"      element={<RequireModule module="papers"><ExploreScreen /></RequireModule>} />
            <Route path="/explore/speakers"    element={<RequireModule module="speakers"><ExploreScreen /></RequireModule>} />
            <Route path="/explore/exhibitors"  element={<RequireModule module="exhibitors"><ExploreScreen /></RequireModule>} />
            <Route path="/explore/sponsors" element={<RequireModule module="sponsors"><ExploreScreen /></RequireModule>} />
            <Route path="/explore/industry-partners" element={<RequireModule module="industry_partners"><ExploreScreen /></RequireModule>} />
            <Route path="/explore/committee" element={<RequireModule module="committee"><ExploreScreen /></RequireModule>} />
            <Route path="/explore/attendees"   element={<RequireModule module="attendees"><ExploreScreen /></RequireModule>} />

            <Route path="/report-builder" element={<RequireModule module="ai_copilot"><ReportBuilderScreen /></RequireModule>} />
            <Route path="/chat-history" element={<RequireModule module="ai_copilot"><ChatHistoryScreen /></RequireModule>} />
            <Route path="/notes/:id"      element={<RequireModule module="notes"><MyNotesScreen /></RequireModule>} />
            <Route path="/notes"          element={<RequireModule module="notes"><MyNotesScreen /></RequireModule>} />

            <Route path="/speaker/:id"   element={<RequireModule module="speakers"><SpeakerProfileScreen /></RequireModule>} />
            <Route path="/paper/:id"     element={<RequireModule module="papers"><PaperDetailsScreen /></RequireModule>} />
            <Route path="/exhibitor/:id" element={<RequireReadyAccount requireEvent><ModuleRouteGuard module={['exhibitors', 'sponsors', 'industry_partners']}><ExhibitorDetailsScreen /></ModuleRouteGuard></RequireReadyAccount>} />
            <Route path="/event/:id"     element={<RequireReadyAccount><EventDetailScreen /></RequireReadyAccount>} />
            <Route path="/attendee/:id"  element={<RequireModule module="attendees"><AttendeeDetailsScreen /></RequireModule>} />

            <Route path="/profile"       element={<RequireReadyAccount><ProfileScreen /></RequireReadyAccount>} />
            <Route path="/change-password" element={<RequireAuth><ChangePasswordScreen /></RequireAuth>} />
            <Route path="/notifications" element={<RequireReadyAccount><NotificationsScreen /></RequireReadyAccount>} />
            <Route path="/map"           element={<RequireModule module="venue_map"><MapScreen /></RequireModule>} />
            <Route path="/recap"         element={<RequireModule module="recap"><ModuleRouteGuard module="ai_copilot"><ConferenceRecapScreen /></ModuleRouteGuard></RequireModule>} />
            {/* Fallback */}
            <Route path="/"  element={<AppEntry />} />
            <Route path="*"  element={<Navigate to="/signin" replace />} />
          </Routes>
        </motion.div>
      </AnimatePresence>
    </AuthGate>
  );
}

function ThemedApp() {
  return (
    <MotionConfig reducedMotion="user">
      <Router>
        <AppShell />
      </Router>
    </MotionConfig>
  );
}

function AppShell() {
  const { theme, isAuthenticated } = useApp();

  useEffect(() => {
    if (isAuthenticated) markWelcomeSeen();
  }, [isAuthenticated]);

  return (
    <>
      {/* Listen during auth hydration too; route only after it has settled. */}
      <NativeAuthLinkListener />
      <AISharingDialog />
      {/* Full viewport background */}
      <div
        className="fixed inset-0 transition-colors duration-300"
        data-theme={theme}
        style={{ backgroundColor: 'var(--app-bg)' }}
      />

      {/* Centered mobile container */}
      <div className="relative z-10 flex items-center justify-center w-full" style={{ height: '100dvh' }}>
        <div
          data-theme={theme}
          className="relative overflow-hidden transition-colors duration-300"
          style={{
            width: '100%',
            maxWidth: '450px',
            height: '100%',
            maxHeight: '100dvh',
            backgroundColor: 'var(--app-bg)',
          }}
        >
          {/* Animated page transitions */}
          <AnimatedRoutes />

          {/* First-run welcome guide overlay */}
          <WelcomeGuide />

          {/* Toast notifications */}
          <Toaster
            position="top-center"
            theme={theme}
            toastOptions={{
              style: {
                background: theme === 'dark' ? '#0F1829' : '#FFFFFF',
                border: `1px solid ${theme === 'dark' ? 'rgba(56,189,248,0.15)' : 'rgba(0,0,0,0.1)'}`,
                color: theme === 'dark' ? '#F1F5F9' : '#0F172A',
                maxWidth: '380px',
              },
            }}
          />
        </div>
      </div>
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppProvider>
        <NotificationProvider>
          <ThemedApp />
        </NotificationProvider>
      </AppProvider>
    </ErrorBoundary>
  );
}
